import React, { useState, useMemo, useEffect, useRef } from 'react';
import apiClient from '../../../services/api';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import SpreadForm from './SpreadForm';
import SpreadContractForm from './SpreadContractForm';
import SpreadFilterControls from './SpreadFilterControls';
import SpreadHeatmapView from './SpreadHeatmapView';
import SpreadDetailTable from './SpreadDetailTable';
import SpreadTourController from './SpreadTourController';

const EMPTY_FILTERS = { tickers: [], expirations: [], spreadTypes: [] };

/** Identity of a spread row — the same key the result views dedupe on. */
const resultKey = (row) => [
  row['Ticker'], row['Spread Key'], row['Long Strike'],
  row['Short Strike'], row['Expiration'],
].join('|');

/**
 * Flatten a DRF error body into readable text.
 *
 * The contract endpoint validates a LIST of nested objects, so its errors
 * arrive as `{legs: [{}, {expiration: ["..."]}]}` — one slot per submitted row,
 * empty for the rows that were fine. Walking it recursively (and labelling
 * list positions as "spread N") is what turns that into something a user can
 * act on instead of "legs: [object Object]".
 */
const formatFieldErrors = (node, path = '') => {
  if (typeof node === 'string') return [path ? `${path}: ${node}` : node];
  if (Array.isArray(node)) {
    return node.flatMap((item, i) => {
      // A list of plain strings is one field's messages, not per-row errors.
      if (typeof item === 'string') return formatFieldErrors(item, path);
      const label = path === 'legs' ? `spread ${i + 1}` : `${path}[${i + 1}]`;
      return formatFieldErrors(item, label);
    });
  }
  if (node && typeof node === 'object') {
    return Object.entries(node).flatMap(([key, value]) => {
      // A cross-field error belongs to the row, not to a field named
      // "non_field_errors" — DRF's key is an implementation detail.
      const next = key === 'non_field_errors' ? path : (path ? `${path} ${key}` : key);
      return formatFieldErrors(value, next);
    });
  }
  return [];
};

const INPUT_MODES = [
  {
    key: 'screener',
    label: 'Screener',
    blurb: 'Scan a range of strikes and expirations to find spreads.',
  },
  {
    key: 'contracts',
    label: 'Specific Contracts',
    blurb: 'Price an exact expiration and strike pair you already have in mind.',
  },
];

/**
 * Vertical Spread Simulator.
 *
 * Simulates call/put debit and credit spreads across an expiration window, an
 * anchor strike position and a spread width — the two-leg counterpart to the
 * Options Screener. Both legs share an expiration; pricing is Black-Scholes per
 * leg (each with its own IV), then netted.
 *
 * Self-contained like ScalpingTab: owns its data/loading/error state and its
 * own ticker input, so it works without first running an analysis.
 */
function SpreadsTab() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const formRef = useRef(null);
  const [scanData, setScanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('heatmap'); // 'heatmap' | 'detail'
  const [inputMode, setInputMode] = useState('screener'); // 'screener' | 'contracts'

  // Shared across both views so favoriting, removing and filtering survive a
  // view switch — the same state ScreenerResponsive lifts for the screener.
  const [favoritedRows, setFavoritedRows] = useState(new Set());
  const [removedRows, setRemovedRows] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sharedDaysToExit, setSharedDaysToExit] = useState(null);
  const [daysToExitMode, setDaysToExitMode] = useState('per-contract');
  const [daysToExitPercent, setDaysToExitPercent] = useState(null);

  const results = useMemo(() => scanData?.results || [], [scanData]);
  const scanErrors = scanData?.errors || [];

  // Seed Days-to-Exit at half the shortest expiration whenever new results
  // arrive — a sensible midpoint the user can then drag either way.
  useEffect(() => {
    if (results.length === 0) return;
    const shortest = Math.min(
      ...results
        .map((r) => parseFloat(r['Days Until Expiration']))
        .filter((d) => d > 0)
    );
    if (!isFinite(shortest)) return;
    setDaysToExitMode('fixed');
    setSharedDaysToExit(Math.max(1, Math.round(shortest * 0.5)));
    setDaysToExitPercent(null);
  }, [results]);

  const defaultDays = useMemo(() => {
    if (results.length === 0) return 30;
    return parseFloat(results[0]['Days to Gain']) || 30;
  }, [results]);

  /**
   * Shared run path for both input modes. They post different payloads to
   * different endpoints but get back the same envelope, so everything from
   * here down is identical.
   *
   * `append` merges into the results already on screen instead of replacing
   * them — how you price a specific spread alongside a screener run without
   * losing the screen.
   */
  const runSimulation = async (url, payload, message, { append = false } = {}) => {
    setLoading(true);
    setError(null);
    setLoadingMessage(message);

    // A replacing run invalidates the previous result set, so clear the
    // row-level state that keys off it rather than leaving stale keys behind.
    // An appending run keeps it, since those rows are still on screen.
    if (!append) {
      setFavoritedRows(new Set());
      setRemovedRows(new Set());
      setShowFavoritesOnly(false);
      setFilters(EMPTY_FILTERS);
    }

    try {
      const response = await apiClient.post(url, payload);
      setScanData((prev) => {
        if (!append || !prev?.results?.length) return response.data;

        // Later rows win on a collision: re-pricing a spread you already have
        // should refresh it, not silently keep the stale quote.
        const merged = new Map(prev.results.map((r) => [resultKey(r), r]));
        (response.data.results || []).forEach((r) => merged.set(resultKey(r), r));
        const results = Array.from(merged.values());

        return {
          ...response.data,
          results,
          total_found: results.length,
          // The union may span strategies even when each run was uniform.
          spread_type: prev.spread_type === response.data.spread_type
            ? response.data.spread_type
            : 'mixed',
          underlying_prices: {
            ...(prev.underlying_prices || {}),
            ...(response.data.underlying_prices || {}),
          },
        };
      });
    } catch (err) {
      console.error('Spread simulation failed:', err);
      const details = err.response?.data?.details;
      const fieldErrors = details ? formatFieldErrors(details) : [];
      if (fieldErrors.length) {
        setError(fieldErrors.join(' • '));
      } else {
        setError(err.response?.data?.error || 'Failed to run spread simulation');
      }
      // An appending run that failed must not discard what is already there.
      if (!append) setScanData(null);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleRun = (payload) => runSimulation(
    '/stock-analyzer/spreads/',
    payload,
    `Pricing spreads for ${payload.tickers.join(', ')}...`,
  );

  const handleRunContracts = (payload, { append = false } = {}) => {
    const tickers = Array.from(new Set(payload.legs.map((l) => l.ticker)));
    return runSimulation(
      '/stock-analyzer/spreads/contracts/',
      payload,
      `Pricing ${payload.legs.length} spread${payload.legs.length === 1 ? '' : 's'} on ${tickers.join(', ')}...`,
      { append },
    );
  };

  const sharedViewProps = {
    results,
    favoritedRows,
    setFavoritedRows,
    removedRows,
    setRemovedRows,
    showFavoritesOnly,
    setShowFavoritesOnly,
    filters,
    defaultDays,
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h2 className="text-xl font-semibold text-white">Spread Simulator</h2>
          {/* The tour walks the screener form specifically — formRef is that
              form's imperative handle, and its steps target its sections. */}
          {inputMode === 'screener' && (
            <SpreadTourController
              hasResults={results.length > 0}
              isMobile={isMobile}
              formRef={formRef}
            />
          )}
        </div>
        <p className="text-gray-400 text-sm">
          Simulate vertical spreads — call and put, debit and credit. Scan a range of
          strikes and expirations, or price an exact pair you already have in mind. Both
          legs share an expiration, so risk and reward are capped on each side: the
          heatmap shows what each spread returns on the capital you actually put at risk.
        </p>
      </div>

      {/* Input mode. Two ways to ask the same question: scan for spreads, or
          price one you have already picked. */}
      <div>
        <div
          data-tour="spread-input-mode"
          style={{
            display: 'flex', gap: 12, marginBottom: 8, padding: 12,
            background: '#1a1a1a', borderRadius: 8,
          }}
        >
          {INPUT_MODES.map((m) => {
            const active = inputMode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => setInputMode(m.key)}
                className={active ? 'active' : ''}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: active ? '2px solid #218aff' : '1px solid #333',
                  background: active ? '#218aff20' : '#0a0a0a',
                  color: active ? '#ffffff' : '#b0b0b0',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <p className="text-gray-500 text-xs mb-4">
          {INPUT_MODES.find((m) => m.key === inputMode)?.blurb}
        </p>
      </div>

      {inputMode === 'screener' ? (
        <SpreadForm ref={formRef} onRun={handleRun} loading={loading} />
      ) : (
        <SpreadContractForm
          onRun={handleRunContracts}
          onRunAppend={(payload) => handleRunContracts(payload, { append: true })}
          canAppend={results.length > 0}
          loading={loading}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-gray-400">{loadingMessage}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {scanData && !loading && (
        <>
          {/* Partial failures show ALONGSIDE results — one bad ticker must
              never discard an otherwise good run. */}
          {scanErrors.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-4">
              <h3 className="text-yellow-400 font-medium mb-2 text-sm">
                Some tickers could not be simulated:
              </h3>
              <ul className="text-yellow-300 text-xs space-y-1">
                {scanErrors.map((e, i) => (
                  <li key={i}>• <strong>{e.ticker}</strong>: {e.error}</li>
                ))}
              </ul>
            </div>
          )}

          {results.length > 0 && (
            <div className="border-t border-gray-700 pt-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-white">Simulation Results</h3>
                <div className="flex items-center gap-3">
                  {scanData.truncated && (
                    <span className="px-3 py-1 bg-yellow-900/30 rounded-full text-yellow-400 text-xs">
                      Showing first {results.length} of {scanData.total_found}
                    </span>
                  )}
                  <span className="px-3 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm">
                    {scanData.total_found} spread{scanData.total_found === 1 ? '' : 's'}
                  </span>
                </div>
              </div>

              {/* View toggle */}
              <div
                data-tour="spread-view-toggle"
                style={{
                  display: 'flex', justifyContent: 'center', gap: 12,
                  marginBottom: 16, padding: 12, background: '#1a1a1a', borderRadius: 8,
                }}
              >
                {[
                  { key: 'heatmap', label: 'Elasticity Heatmap' },
                  { key: 'detail', label: 'Detail View' },
                ].map((v) => {
                  const active = viewMode === v.key;
                  return (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => setViewMode(v.key)}
                      data-tour={`spread-view-${v.key}`}
                      className={active ? 'active' : ''}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: active ? '2px solid #218aff' : '1px solid #333',
                        background: active ? '#218aff20' : '#0a0a0a',
                        color: active ? '#ffffff' : '#b0b0b0',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 14,
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>

              <SpreadFilterControls
                results={results}
                filters={filters}
                onChange={setFilters}
              />

              {viewMode === 'heatmap' ? (
                <SpreadHeatmapView
                  {...sharedViewProps}
                  sharedDaysToExit={sharedDaysToExit}
                  setSharedDaysToExit={setSharedDaysToExit}
                  daysToExitMode={daysToExitMode}
                  setDaysToExitMode={setDaysToExitMode}
                  daysToExitPercent={daysToExitPercent}
                  setDaysToExitPercent={setDaysToExitPercent}
                />
              ) : (
                <SpreadDetailTable {...sharedViewProps} />
              )}
            </div>
          )}

          {results.length === 0 && scanErrors.length === 0 && (
            <div className="text-center py-12 border-t border-gray-700">
              <p className="text-gray-400">
                {inputMode === 'screener'
                  ? 'No tradeable spreads matched those parameters.'
                  : 'That spread could not be priced.'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {inputMode === 'screener'
                  ? `Try a wider spread, a different anchor strike, or a longer expiration —
                     narrow widths on a coarse strike grid can collapse both legs onto the
                     same contract.`
                  : `One of the legs has no usable quote. Illiquid strikes often have no
                     trade, no fair value and no two-sided market — try strikes nearer the
                     money or a more actively traded expiration.`}
              </p>
            </div>
          )}
        </>
      )}

      {!scanData && !loading && !error && (
        <div className="text-center py-12 border-t border-gray-700">
          <div className="text-5xl mb-3">📐</div>
          <p className="text-gray-400">
            {inputMode === 'screener'
              ? 'Pick your tickers and a spread type, then run a simulation.'
              : 'Pick a ticker, an expiration and your two strikes, then price the spread.'}
          </p>
        </div>
      )}
    </div>
  );
}

export default SpreadsTab;
